import Router from 'next/router';
import { memo, useCallback, useMemo } from 'react';
import { useMount } from 'react-use';

import { useTrackClickedUpgradeButton, useTrackPlanUpgradeModalClicked } from '@air/analytics';
import { CheckIcon, CircleSlashIcon } from '@air/design-icons';
import { Badge, type BadgeProps } from '@air/primitive-badge';
import { Button, type ButtonProps } from '@air/primitive-button';
import { useAirModal, useModalContext } from '@air/provider-modal';
import { cn } from '@air/utils-tailwind';

import { DemoFormModal } from '~/components/Modals/DemoFormModal';
import { ManageCreditPacksModal } from '~/components/Modals/ManageCreditPacks/ManageCreditPacksModal';
import { PlanChangeConfirmationModal } from '~/components/Modals/PlansModal/PlanChangeConfirmationModal';
import {
  formatMonthlyPrice,
  type PlanInfoType,
  type UpgradePlanModalBillingCycle,
} from '~/components/Modals/PlansModal/utils/planInfo';
import { Routes } from '~/constants/routes';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';

export type UpgradePlanModalPlanCardProps = PlanInfoType & {
  availablePlans: PlanInfoType[];
  billingCycle: UpgradePlanModalBillingCycle;
  onClose: () => void;
};

export const UpgradePlanModalPlanCard = memo(
  ({
    planId,
    planTitle,
    action,
    prices,
    tagline,
    pricingModel,
    credits,
    options,
    customPriceDescription,
    isCurrentPlan,
    isRecommended,
    secondaryCta,
    previousPlan,
    stripePlanIds,
    billingCycle,
    availablePlans,
    onClose,
  }: UpgradePlanModalPlanCardProps) => {
    const { trackClickedUpgradeButton } = useTrackClickedUpgradeButton();
    const { trackPlanUpgradeModalClicked } = useTrackPlanUpgradeModalClicked();
    const { dispatch: modalDispatch } = useModalContext();
    const [showDemoFormModal] = useAirModal(DemoFormModal);
    const [showPlanChangeConfirmationModal] = useAirModal(PlanChangeConfirmationModal);
    const [showManageCreditPacksModal] = useAirModal(ManageCreditPacksModal);
    const { data: subscription } = useSubscriptionContext();

    useMount(() => {
      if (action === 'self-service-upgrade-via-checkout') {
        Router.prefetch(Routes.workspaceAdmin.checkout);
      }
    });

    const onSelfServiceDowngrade = useCallback(() => {
      showPlanChangeConfirmationModal({
        action: 'self-service-downgrade',
        planId,
        planTitle,
        prices,
        pricingModel,
        stripePlanIds,
      });
    }, [showPlanChangeConfirmationModal, planId, planTitle, prices, pricingModel, stripePlanIds]);

    const onBookDemo = useCallback(() => {
      showDemoFormModal({ trigger: planId === 'enterprise' ? 'plans_modal_enterprise' : 'plans_modal_business' });
    }, [planId, showDemoFormModal]);

    const handleAddCredits = useCallback(() => {
      showManageCreditPacksModal({ source: 'plans_modal' });
    }, [showManageCreditPacksModal]);

    const navigateToCheckout = useCallback(() => {
      onClose();

      /**
       * Since the plan modal generally overlaps another modal, only the highest level modal is closed.
       * Therefore we need to close all modals with the ModalContext before redirecting to checkout.
       */
      modalDispatch({ type: 'hide-all' });

      Router.push({
        pathname: Routes.workspaceAdmin.checkout,
        search: `planId=${planId}`,
      });
    }, [planId, modalDispatch, onClose]);

    const onSelfServiceUpgrade = useCallback(() => {
      trackClickedUpgradeButton({ plan: planId });
      if (action === 'self-service-upgrade-via-checkout') {
        navigateToCheckout();
      } else {
        trackPlanUpgradeModalClicked({
          new_plan: planId,
          old_plan: subscription?.plan ?? 'basic',
        });
        showPlanChangeConfirmationModal({
          action: 'self-service-upgrade',
          planId,
          planTitle,
          prices,
          pricingModel,
          stripePlanIds,
        });
      }
    }, [
      action,
      navigateToCheckout,
      planId,
      subscription?.plan,
      trackClickedUpgradeButton,
      trackPlanUpgradeModalClicked,
      showPlanChangeConfirmationModal,
      planTitle,
      prices,
      pricingModel,
      stripePlanIds,
    ]);

    const buttonProps = useMemo<ButtonProps>(() => {
      switch (action) {
        case 'current-plan':
          return {
            children: 'Current Plan',
            disabled: true,
          };
        case 'add-credits':
          return {
            children: 'Add more monthly credits',
            color: 'blue',
            appearance: 'filled',
            onClick: handleAddCredits,
          };
        case 'self-service-downgrade':
          return {
            children: `Switch to ${planTitle}`,
            color: 'gray',
            onClick: onSelfServiceDowngrade,
          };
        case 'self-service-upgrade':
        case 'self-service-upgrade-via-checkout':
          return {
            children: 'Get started',
            onClick: onSelfServiceUpgrade,
            appearance: availablePlans.some((plan) => plan.action === 'add-credits') ? 'outline' : 'filled',
          };
        case 'book-demo':
          return {
            appearance: 'outline',
            children: 'Contact sales',
            color: 'gray',
            onClick: onBookDemo,
          };
      }
    }, [availablePlans, action, planTitle, handleAddCredits, onBookDemo, onSelfServiceDowngrade, onSelfServiceUpgrade]);

    const planFeaturesLabel = useMemo(() => {
      if (!previousPlan) {
        return 'All plans include:';
      }
      if (previousPlan.planId === 'creator') {
        return 'Free plan features, plus:';
      }

      return `Everything in ${previousPlan.planTitle}, plus:`;
    }, [previousPlan]);

    const badgeProps = useMemo<BadgeProps | undefined>(() => {
      if (isCurrentPlan) {
        return {
          children: 'Current plan',
          color: 'gray',
        };
      }

      if (isRecommended) {
        return {
          children: 'Most popular',
          color: 'light-blue',
        };
      }
    }, [isCurrentPlan, isRecommended]);

    return (
      <div
        className={cn(
          'relative flex w-full max-w-md flex-col gap-6 rounded-12 p-6',
          isCurrentPlan ? 'bg-transparent' : 'border ads:border-default border-gray-4 ads:bg-base bg-gray-1',
          action === 'self-service-downgrade' && 'ads:bg-raised bg-gray-2',
        )}
        data-plan={planId}
        data-testid="PLAN_CONTAINER"
      >
        {/* biome-ignore lint/suspicious/noUnnecessaryConditions: badgeProps is optional from useMemo */}
        {badgeProps && <Badge {...badgeProps} className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2" />}

        <div>
          <h3 className="font-control ads:text-primary text-36 text-gray-12">{planTitle}</h3>
          {tagline && <p className="ads:text-metadata text-14 text-gray-10">{tagline}</p>}
        </div>

        <div className="flex flex-col gap-4">
          {customPriceDescription ? (
            <p className="font-semibold ads:text-primary text-36 text-gray-12 leading-none">{customPriceDescription}</p>
          ) : (
            <div className="flex items-end gap-2">
              <span className="font-semibold ads:text-primary text-36 text-gray-12 leading-none">
                {formatMonthlyPrice({ billingCycle, prices })}
              </span>{' '}
              <div className="flex flex-col font-semibold ads:text-metadata text-12 text-gray-9">
                <span>per month</span>
                <span>billed {billingCycle === 'annual' ? 'annually' : 'monthly'}</span>
              </div>
            </div>
          )}
          <Button
            appearance="filled"
            color="blue"
            data-action={action}
            data-plan={planId}
            data-testid="UPGRADE_PLAN_MODAL_CARD_BUTTON"
            size="large"
            {...buttonProps}
          />
        </div>

        <div className="flex flex-col gap-3 text-14">
          {!!credits && (
            <div className="flex items-start gap-2">
              <CircleSlashIcon className="mt-0.5 flex-shrink-0 ads:text-ads-amber-9 text-amber-9" size="sm" />
              <div className="font-semibold ads:text-metadata text-gray-10">{credits}</div>
            </div>
          )}
          {!!options && (
            <div className="flex flex-col gap-2">
              <p className="font-medium ads:text-primary text-gray-12">{planFeaturesLabel}</p>
              {options.map((option, index) => (
                <div className="flex items-start gap-2" key={index}>
                  <CheckIcon className="mt-0.5 flex-shrink-0 ads:text-ads-grass-9 text-grass-9" size="sm" />
                  <div className="ads:text-metadata text-gray-10">{option}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {secondaryCta === 'book-demo' && (
          <div className="mt-auto flex justify-center">
            <Button appearance="outline" color="gray" onClick={onBookDemo} size="medium" type="button">
              Get a demo
            </Button>
          </div>
        )}
      </div>
    );
  },
);

UpgradePlanModalPlanCard.displayName = 'UpgradePlanModalPlanCard';
